import React from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, FormControl, MenuItem, Pagination, Select } from '@mui/material';

import SystemDesignButton from './Button';
import { SCOUT_TEAL } from '../../ThemeContext/constants';
import {
  pagination,
  paginationContainer,
  rowSelect,
  rowSelectContainer
} from '../shared/styleClasses';

import type { SelectChangeEvent } from '@mui/material';

import type { Cursor, CursorBoundary, Total } from '../api/cursorPagination';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

export type PaginationBarSize = 'small' | 'medium' | 'large';

interface PaginationSizeStyle {
  itemSize?: string;
  itemFontSize?: string;
  itemBorderRadius?: string;
  iconSize?: string;
  labelFontSize?: string;
  labelLineHeight?: string;
  totalFontSize?: string;
  selectFontSize?: string;
  selectHeight?: string;
  selectWidth?: string;
  selectLineHeight?: string;
  selectPadding?: string;
}

// `medium` is intentionally empty: it must render identically to the pre-existing,
// un-sized PaginationBar (base `rowSelect`/`pagination` styles, no inline overrides).
const PAGINATION_SIZE_STYLES: Record<PaginationBarSize, PaginationSizeStyle> = {
  small: {
    itemSize: '24px',
    itemFontSize: '12px',
    itemBorderRadius: '12px',
    iconSize: '16px',
    labelFontSize: '12px',
    labelLineHeight: '16px',
    totalFontSize: '12px',
    selectFontSize: '10px',
    selectHeight: '24px',
    selectWidth: '28px',
    selectLineHeight: '13px',
    selectPadding: '5px 8px'
  },
  medium: {},
  large: {
    itemSize: '48px',
    itemBorderRadius: '24px',
    iconSize: '24px',
    selectFontSize: '14px',
    selectHeight: '40px',
    selectWidth: '45px',
    selectLineHeight: '18px',
    selectPadding: '9px 14px'
  }
};

// The numbered bar's props, unchanged from before cursor mode existed.
export interface ExistingPaginationBarProps {
  page: number; // 1-based
  pageSize: number;
  totalPages: number;
  totalItems: number;
  size?: PaginationBarSize;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  rowsPerPageLabelId?: string;
  rowsPerPageSelectId?: string;
  firstOptionId?: string;
  secondOptionId?: string;
  paginationId?: string;
  totalId?: string;
  rowsPerPageSelectTestId?: string;
  firstOptionTestId?: string;
  secondOptionTestId?: string;
  paginationContainerTestId?: string;
  totalTestId?: string;
}

// Calls without `mode` render the numbered bar exactly as before.
export type NumberedBarProps = ExistingPaginationBarProps & { mode?: 'numbered'; total?: Total };

type SharedBarProps = Pick<
  ExistingPaginationBarProps,
  | 'pageSize'
  | 'onPageSizeChange'
  | 'pageSizeOptions'
  | 'size'
  | 'rowsPerPageLabelId'
  | 'rowsPerPageSelectId'
  | 'firstOptionId'
  | 'secondOptionId'
  | 'paginationId'
  | 'totalId'
  | 'rowsPerPageSelectTestId'
  | 'firstOptionTestId'
  | 'secondOptionTestId'
  | 'paginationContainerTestId'
  | 'totalTestId'
>;

// Keyset (cursor) navigation: previous/next only, no page numbers, first/last or jumps.
export type CursorBarProps = SharedBarProps & {
  mode: 'cursor';
  nextCursor: Cursor | null;
  previousCursor: Cursor | null;
  total: Total;
  isFetching: boolean;
  onNavigate: (boundary: CursorBoundary) => void;
  previousButtonId?: string;
  nextButtonId?: string;
};

export type PaginationBarProps = NumberedBarProps | CursorBarProps;

const styles = {
  paginationParent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
    width: '100%'
  },
  rowSelectContainer,
  paginationContainer,
  pagination,
  rowSelect,
  paginationMenuItems: {
    '& li': {
      fontFamily: 'Source Sans Pro'
    }
  },
  cursorNavigation: {
    display: 'flex',
    alignItems: 'center',
    columnGap: 1,
    marginRight: '24px'
  },
  // Read by screen readers only, so a page change is not silent.
  liveRegion: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px'
  }
};

const pageSizeOptionId = (
  index: number,
  firstOptionId: string,
  secondOptionId: string
): string | undefined => {
  if (index === 0) {
    return firstOptionId;
  }
  if (index === 1) {
    return secondOptionId;
  }
  return undefined;
};

const pageSizeOptionTestId = (
  index: number,
  firstOptionTestId?: string,
  secondOptionTestId?: string
): string | undefined => {
  if (index === 0) {
    return firstOptionTestId;
  }
  if (index === 1) {
    return secondOptionTestId;
  }
  return undefined;
};

const compactNumber = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 2
});

// An approximate total reads "About 9.85M" and a proven lower bound "10,000+ results"; an
// omitted total shows nothing rather than a fake "0 Total".
export const formatCursorTotal = (total: Total): string | null => {
  switch (total.kind) {
    case 'exact':
      return `${total.value} Total`;
    case 'approximate':
      return `About ${compactNumber.format(total.value)}`;
    case 'capped':
      return `${total.value.toLocaleString('en')}+ results`;
    default:
      return null;
  }
};

interface RowsPerPageSelectProps {
  pageSize: number;
  pageSizeOptions: number[];
  paginationSize: PaginationSizeStyle;
  onPageSizeChange: (pageSize: number) => void;
  rowsPerPageLabelId: string;
  rowsPerPageSelectId: string;
  firstOptionId: string;
  secondOptionId: string;
  rowsPerPageSelectTestId?: string;
  firstOptionTestId?: string;
  secondOptionTestId?: string;
}

const RowsPerPageSelect = ({
  pageSize,
  pageSizeOptions,
  paginationSize,
  onPageSizeChange,
  rowsPerPageLabelId,
  rowsPerPageSelectId,
  firstOptionId,
  secondOptionId,
  rowsPerPageSelectTestId,
  firstOptionTestId,
  secondOptionTestId
}: RowsPerPageSelectProps): React.ReactElement => {
  const hasSizeOverrides = Object.keys(paginationSize).length > 0;

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    onPageSizeChange(Number(event.target.value));
  };

  return (
    <Box sx={styles.rowSelectContainer}>
      <span
        id={rowsPerPageLabelId}
        style={
          paginationSize.labelFontSize
            ? {
                fontSize: paginationSize.labelFontSize,
                lineHeight: paginationSize.labelLineHeight
              }
            : undefined
        }
      >
        Rows per page:{' '}
      </span>
      <FormControl>
        <Select
          displayEmpty
          data-testid={rowsPerPageSelectTestId}
          id={rowsPerPageSelectId}
          labelId={rowsPerPageLabelId}
          MenuProps={{ sx: styles.paginationMenuItems }}
          onChange={handlePageSizeChange}
          value={pageSize}
          sx={{
            ...styles.rowSelect,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${SCOUT_TEAL} !important`
            },
            ...(hasSizeOverrides && {
              fontSize: paginationSize.selectFontSize,
              height: paginationSize.selectHeight,
              width: paginationSize.selectWidth,
              lineHeight: paginationSize.selectLineHeight,
              '& .MuiOutlinedInput-input': {
                lineHeight: `${paginationSize.selectLineHeight} !important`,
                minHeight: `${paginationSize.selectLineHeight} !important`,
                padding: `${paginationSize.selectPadding} !important`,
                textAlign: 'center'
              },
              // MUI's notched-outline fieldset defaults to `top: -5px` to make room for a
              // floating InputLabel's notch. This Select has no MUI InputLabel (the label is
              // an external <span>), so that offset just makes the painted border taller than
              // the box, pushing all the slack to the top. Reset it so the border matches,
              // while keeping `rowSelect`'s own border colour (spread first, so it doesn't
              // get clobbered by this shallow merge).
              '& .MuiOutlinedInput-notchedOutline': {
                ...styles.rowSelect['& .MuiOutlinedInput-notchedOutline'],
                top: 0
              }
            })
          }}
        >
          {pageSizeOptions.map((option, index) => (
            <MenuItem
              key={option}
              data-testid={pageSizeOptionTestId(index, firstOptionTestId, secondOptionTestId)}
              id={pageSizeOptionId(index, firstOptionId, secondOptionId)}
              value={option}
            >
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const NumberedPaginationBar = ({
  page,
  pageSize,
  totalPages,
  totalItems,
  total,
  size = 'medium',
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  rowsPerPageLabelId = 'rows-per-page-label',
  rowsPerPageSelectId = 'rows-per-page',
  firstOptionId = 'first-rpp-item',
  secondOptionId = 'second-rpp-item',
  paginationId = 'pagination-menu',
  totalId = 'total-jobs',
  rowsPerPageSelectTestId,
  firstOptionTestId,
  secondOptionTestId,
  paginationContainerTestId,
  totalTestId
}: Omit<NumberedBarProps, 'mode'>): React.ReactElement => {
  const paginationSize = PAGINATION_SIZE_STYLES[size];
  const hasSizeOverrides = Object.keys(paginationSize).length > 0;

  return (
    <Box sx={styles.paginationParent}>
      <RowsPerPageSelect
        firstOptionId={firstOptionId}
        firstOptionTestId={firstOptionTestId}
        onPageSizeChange={onPageSizeChange}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        paginationSize={paginationSize}
        rowsPerPageLabelId={rowsPerPageLabelId}
        rowsPerPageSelectId={rowsPerPageSelectId}
        rowsPerPageSelectTestId={rowsPerPageSelectTestId}
        secondOptionId={secondOptionId}
        secondOptionTestId={secondOptionTestId}
      />
      <Box data-testid={paginationContainerTestId} sx={styles.paginationContainer}>
        <Pagination
          count={totalPages}
          id={paginationId}
          onChange={(_event, nextPage) => onPageChange(nextPage)}
          page={page}
          siblingCount={0}
          sx={{
            ...styles.pagination,
            ...(hasSizeOverrides && {
              '&& .MuiPaginationItem-root:not(.MuiPaginationItem-ellipsis)': {
                height: paginationSize.itemSize,
                minWidth: paginationSize.itemSize,
                fontSize: paginationSize.itemFontSize,
                borderRadius: paginationSize.itemBorderRadius
              },
              '& .MuiPaginationItem-ellipsis': {
                fontSize: paginationSize.itemFontSize
              },
              '& .MuiPaginationItem-icon': {
                fontSize: paginationSize.iconSize
              }
            })
          }}
        />
        {total?.kind !== 'omitted' && (
          <span
            data-testid={totalTestId}
            id={totalId}
            style={
              paginationSize.totalFontSize ? { fontSize: paginationSize.totalFontSize } : undefined
            }
          >
            {!total || total.kind === 'exact' ? <>{totalItems} Total</> : formatCursorTotal(total)}
          </span>
        )}
      </Box>
    </Box>
  );
};

const CursorPaginationBar = ({
  pageSize,
  size = 'medium',
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  rowsPerPageLabelId = 'rows-per-page-label',
  rowsPerPageSelectId = 'rows-per-page',
  firstOptionId = 'first-rpp-item',
  secondOptionId = 'second-rpp-item',
  paginationId = 'pagination-menu',
  totalId = 'total-jobs',
  rowsPerPageSelectTestId,
  firstOptionTestId,
  secondOptionTestId,
  paginationContainerTestId,
  totalTestId,
  nextCursor,
  previousCursor,
  total,
  isFetching,
  onNavigate,
  previousButtonId = 'pagination-previous',
  nextButtonId = 'pagination-next'
}: Omit<CursorBarProps, 'mode'>): React.ReactElement => {
  const paginationSize = PAGINATION_SIZE_STYLES[size];
  const totalLabel = formatCursorTotal(total);

  return (
    <Box sx={styles.paginationParent}>
      <RowsPerPageSelect
        firstOptionId={firstOptionId}
        firstOptionTestId={firstOptionTestId}
        onPageSizeChange={onPageSizeChange}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        paginationSize={paginationSize}
        rowsPerPageLabelId={rowsPerPageLabelId}
        rowsPerPageSelectId={rowsPerPageSelectId}
        rowsPerPageSelectTestId={rowsPerPageSelectTestId}
        secondOptionId={secondOptionId}
        secondOptionTestId={secondOptionTestId}
      />
      <Box data-testid={paginationContainerTestId} sx={styles.paginationContainer}>
        <Box
          aria-label="pagination navigation"
          component="nav"
          id={paginationId}
          sx={styles.cursorNavigation}
        >
          <SystemDesignButton
            aria-label="Go to previous page"
            buttonVariant="secondary"
            disabled={previousCursor === null || isFetching}
            icon={<ChevronLeftIcon />}
            iconPosition="leading"
            id={previousButtonId}
            size={size}
            onClick={() => {
              if (previousCursor !== null) onNavigate({ kind: 'before', cursor: previousCursor });
            }}
          >
            Previous
          </SystemDesignButton>
          <SystemDesignButton
            aria-label="Go to next page"
            buttonVariant="secondary"
            disabled={nextCursor === null || isFetching}
            icon={<ChevronRightIcon />}
            iconPosition="trailing"
            id={nextButtonId}
            size={size}
            onClick={() => {
              if (nextCursor !== null) onNavigate({ kind: 'after', cursor: nextCursor });
            }}
          >
            Next
          </SystemDesignButton>
        </Box>
        {totalLabel !== null ? (
          <span
            data-testid={totalTestId}
            id={totalId}
            style={
              paginationSize.totalFontSize ? { fontSize: paginationSize.totalFontSize } : undefined
            }
          >
            {totalLabel}
          </span>
        ) : null}
        <Box aria-live="polite" component="span" role="status" sx={styles.liveRegion}>
          {isFetching ? 'Loading results' : 'Results loaded'}
        </Box>
      </Box>
    </Box>
  );
};

const isCursorBarProps = (props: PaginationBarProps): props is CursorBarProps =>
  props.mode === 'cursor';

const PaginationBar = (props: PaginationBarProps): React.ReactElement =>
  isCursorBarProps(props) ? (
    <CursorPaginationBar {...props} />
  ) : (
    <NumberedPaginationBar {...props} />
  );

export default PaginationBar;
